import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { brandInfo, faqList } from '@/mocks/brand';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9pi0fmb4qjhggaepus0';

export default function AboutPage() {
  const [activeTab, setActiveTab] = useState<'brand' | 'faq' | 'contact'>('brand');

  const tabs = [
    { key: 'brand' as const, label: '品牌介绍', icon: 'ri-building-2-line' },
    { key: 'faq' as const, label: '常见问题', icon: 'ri-question-line' },
    { key: 'contact' as const, label: '联系我们', icon: 'ri-mail-line' },
  ];

  return (
    <>
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于我们 - 51吃瓜',
          description: brandInfo.description,
          url: 'https://51chigua.com/about',
          isPartOf: {
            '@type': 'WebSite',
            name: '51吃瓜',
            url: 'https://51chigua.com',
          },
        }}
      />
      <SchemaOrg
        type="FAQPage"
        data={{
          mainEntity: faqList.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
            },
          })),
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <section className="w-full bg-background-100 border-b border-background-200/70">
            <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16 text-center">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
                关于51吃瓜
              </h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                国内领先的娱乐资讯聚合平台，致力于为广大网友提供最新、最全、最有趣的吃瓜内容。
              </p>
            </div>
          </section>

          <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="flex items-center justify-center gap-1 bg-background-100 rounded-xl p-1 mt-8 mb-8">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`whitespace-nowrap flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                    activeTab === tab.key
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  <i className={`${tab.icon} text-base`}></i>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Brand Tab */}
          {activeTab === 'brand' && (
            <section className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 pb-16">
              <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8 mb-8">
                <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">品牌故事</h2>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">{brandInfo.description}</p>
                <div className="flex flex-wrap gap-4 text-sm">
                  <span className="text-foreground-500">成立时间：<strong className="text-foreground-900">{brandInfo.founded}</strong></span>
                </div>
              </div>

              <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8 mb-8">
                <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">我们的使命</h2>
                <p className="text-sm text-foreground-700 leading-relaxed">{brandInfo.mission}</p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                {brandInfo.stats.map((stat) => (
                  <div key={stat.label} className="bg-background-50 rounded-xl border border-background-200/70 p-5 text-center">
                    <div className="text-xl md:text-2xl font-heading font-bold text-primary-500 mb-1">{stat.value}</div>
                    <div className="text-xs text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8">
                <h2 className="text-lg font-heading font-bold text-foreground-900 mb-6">发展历程</h2>
                <div className="space-y-0">
                  {brandInfo.milestones.map((m, i) => (
                    <div key={i} className="flex gap-4 pb-6 relative">
                      {i < brandInfo.milestones.length - 1 && (
                        <div className="absolute left-[19px] top-10 bottom-0 w-px bg-background-200"></div>
                      )}
                      <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-1">
                        <i className="ri-check-line text-accent-600"></i>
                      </div>
                      <div>
                        <span className="text-xs font-bold text-accent-600 bg-accent-100 px-2 py-0.5 rounded-full">{m.year}</span>
                        <h3 className="text-sm font-bold text-foreground-900 mt-1 mb-1">{m.title}</h3>
                        <p className="text-xs text-foreground-500 leading-relaxed">{m.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* FAQ Tab */}
          {activeTab === 'faq' && (
            <section className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 pb-16">
              <div className="space-y-4">
                {faqList.map((faq, index) => (
                  <details key={index} className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
                    <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                      <h3 className="text-sm font-bold text-foreground-900 pr-4">{faq.question}</h3>
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                    </summary>
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </section>
          )}

          {/* Contact Tab */}
          {activeTab === 'contact' && (
            <section className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 pb-16">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8">
                  <h2 className="text-lg font-heading font-bold text-foreground-900 mb-6">联系方式</h2>
                  <div className="space-y-5">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                        <i className="ri-map-pin-line text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-400 mb-0.5">公司地址</p>
                        <p className="text-sm text-foreground-800 font-medium">{brandInfo.contact.address}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                        <i className="ri-mail-line text-accent-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-400 mb-0.5">电子邮箱</p>
                        <p className="text-sm text-foreground-800 font-medium">{brandInfo.contact.email}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 flex-shrink-0">
                        <i className="ri-phone-line text-secondary-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-400 mb-0.5">联系电话</p>
                        <p className="text-sm text-foreground-800 font-medium">{brandInfo.contact.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                        <i className="ri-wechat-line text-accent-600"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-400 mb-0.5">微信公众号</p>
                        <p className="text-sm text-foreground-800 font-medium">{brandInfo.contact.wechat}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8">
                  <h2 className="text-lg font-heading font-bold text-foreground-900 mb-6">给我们留言</h2>
                  <ContactForm />
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}

function ContactForm() {
  const [formStatus, setFormStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormStatus(null);

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setSubmitting(false);
      setFormStatus({ type: 'success', message: '留言已提交成功，我们会尽快回复！' });
      return;
    }

    try {
      const response = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setFormStatus({ type: 'success', message: '留言已提交成功，我们会尽快回复！' });
        form.reset();
      } else {
        const serverMsg = (parsed as Record<string, unknown>)?.meta && (parsed as Record<string, unknown>)?.message
          ? String((parsed as Record<string, Record<string, unknown>>).meta.message)
          : (parsed as Record<string, unknown>)?.message
            ? String((parsed as Record<string, unknown>).message)
            : responseText;
        if (serverMsg.includes('spam')) {
          setFormStatus({ type: 'success', message: '留言已提交成功，我们会尽快回复！' });
        } else {
          setFormStatus({ type: 'error', message: serverMsg || '提交失败，请稍后再试。' });
        }
      }
    } catch {
      setFormStatus({ type: 'error', message: '网络错误，请稍后重试。' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} data-readdy-form="true">
      <div className="space-y-4">
        <div>
          <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1">姓名</label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            className="w-full px-3 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-900 bg-background-50 focus:outline-none focus:border-primary-300 transition-colors"
            placeholder="请输入您的姓名"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1">邮箱</label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            className="w-full px-3 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-900 bg-background-50 focus:outline-none focus:border-primary-300 transition-colors"
            placeholder="请输入您的邮箱"
          />
        </div>
        <div>
          <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1">留言内容</label>
          <textarea
            id="contact-message"
            name="message"
            required
            rows={4}
            maxLength={500}
            className="w-full px-3 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-900 bg-background-50 focus:outline-none focus:border-primary-300 transition-colors resize-none"
            placeholder="请输入您的留言内容（最多500字）"
          ></textarea>
          <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
        </div>
        <div className="honeypot-field">
          <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
        </div>
        {formStatus && (
          <div className={`text-sm p-3 rounded-lg ${formStatus.type === 'success' ? 'bg-primary-50 text-primary-700' : 'bg-accent-100 text-accent-800'}`}>
            {formStatus.message}
          </div>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="whitespace-nowrap w-full bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-background-50 py-3 rounded-lg text-sm font-bold transition-colors cursor-pointer"
        >
          {submitting ? '提交中...' : '提交留言'}
        </button>
      </div>
    </form>
  );
}